<script setup>
// 活動站共用頁首＝頂部橘色資訊條 + 導覽列，整體高度為 --site-header-h。
// 其他頁面用 <SiteHeader active="rules" /> 指定目前所在的選單，
// 需要換選單內容時傳 navs 覆蓋預設值。
import logoImg from '~/assets/img/logo.png'
import hostIcon from '~/assets/img/icon-host.png'
import accountIcon from '~/assets/img/icon-account.png'

const props = defineProps({
  active: { type: String, default: '' },
  // 頂部橘色資訊條：主辦單位
  hostName: { type: String, default: '運動部全民運動署' },
  // 投票挺步道的列表與詳情同屬 pages/trails/，所以指向 /trails 而非 /vote
  // （defineProps 會被提升到 setup() 外，預設值不能引用外面宣告的變數，只能寫在這裡）
  navs: {
    type: Array,
    default: () => [
      { key: 'home', label: '首頁', to: '/' },
      { key: 'rules', label: '活動辦法', to: '/rules' },
      { key: 'vote', label: '親山步道票選', to: '/trails' },
      { key: 'stamp', label: '主題形象蓋章', to: '/stamp' },
      // TODO: 山林問答
      // { key: 'challenge', label: '山林問答挑戰', to: '/challenge' },
      { key: 'ranking', label: '票選即時統計', to: '/rankings' },
      { key: 'winner', label: '中獎名單', to: '/winners' }
    ]
  },
  siteName: { type: String, default: '115年全民親山步道票選活動' },
  // 未登入顯示 guestText（點了開登入彈窗），登入後才換成 loginText（進會員中心）
  guestText: { type: String, default: '網路票選登入' },
  loginText: { type: String, default: '我的帳號' },
  ctaText: { type: String, default: '我要投票' }
})

const { isLogged } = useAuth()
const { openLogin } = useLoginDialog()

// 「活動辦法」一律露出：沒有進行中的活動時，那一頁顯示的是上一檔的內容。
// 「中獎名單」則是公佈時間到了才露出（後端判斷，口徑同中獎名單頁的 announced）
const { winnersAnnounced } = await useActivityStatus()
const visibleNavs = computed(() =>
  props.navs.filter((i) => {
    if (i.key === 'winner') return winnersAnnounced.value
    return true
  })
)

const menuOpen = ref(false)

// 手機版選單裡按登入，選單要先收起來，否則彈窗開了後面還攤著一片選單
function onLogin() {
  menuOpen.value = false
  openLogin()
}
</script>

<template>
  <div class="site-head">
    <!-- 頂部橘色資訊條：靠右對齊，和導覽列共用 --site-max 的內容寬度 -->
    <div class="site-top">
      <div class="site-top-inner">
        <span class="top-item">
          <img class="top-ico" :src="hostIcon" width="16" height="16" alt="" aria-hidden="true" />
          主辦單位：{{ hostName }}
        </span>
      </div>
    </div>

    <header class="site-nav">
      <div class="site-nav-inner">
        <NuxtLink class="brand" to="/">
          <img :src="logoImg" :alt="siteName" width="156" height="32" />
        </NuxtLink>

        <nav class="links" :class="{ 'is-open': menuOpen }">
          <NuxtLink
            v-for="item in visibleNavs"
            :key="item.key"
            class="link"
            :class="{ 'is-active': item.key === active }"
            :to="item.to"
            @click="menuOpen = false"
          >
            {{ item.label }}
          </NuxtLink>
          <div class="actions-m">
            <NuxtLink v-if="isLogged" class="nav-acc" to="/member">
              <img
                class="acc-ico"
                :src="accountIcon"
                width="22"
                height="22"
                alt=""
                aria-hidden="true"
              />
              {{ loginText }}
            </NuxtLink>
            <button v-else class="nav-btn nav-btn-line" type="button" @click="onLogin">
              {{ guestText }}
            </button>
            <!-- <NuxtLink class="nav-btn nav-btn-solid" to="/trails">{{ ctaText }}</NuxtLink> -->
          </div>
        </nav>

        <div class="actions">
          <NuxtLink v-if="isLogged" class="nav-acc" to="/member">
            <img
              class="acc-ico"
              :src="accountIcon"
              width="22"
              height="22"
              alt=""
              aria-hidden="true"
            />
            {{ loginText }}
          </NuxtLink>
          <button v-else class="nav-btn nav-btn-line" type="button" @click="onLogin">
            {{ guestText }}
          </button>
          <!-- <NuxtLink class="nav-btn nav-btn-solid" to="/trails">{{ ctaText }}</NuxtLink> -->
        </div>

        <button
          class="toggle"
          type="button"
          :aria-expanded="menuOpen"
          aria-label="開啟選單"
          @click="menuOpen = !menuOpen"
        >
          <span :class="{ 'is-open': menuOpen }"></span>
        </button>
      </div>
    </header>

    <LoginDialog />
  </div>
</template>

<style scoped>
.site-head {
  position: relative;
  z-index: 20;
  font-family: var(--site-font);
  color: var(--site-ink);
  animation: navDrop 0.75s cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

.site-top {
  height: var(--site-topbar-h);
  background: var(--site-orange);
  color: #fff;
}

/* 資訊條靠右，但用和導覽列同一組容器寬度，右邊界才會對齊「我要投票」 */
.site-top-inner {
  max-width: var(--site-max);
  height: 100%;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0.3px;
}

.top-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.top-item .top-ico {
  display: block;
  flex: none;
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.site-nav {
  position: relative;
  height: var(--site-nav-h);
  background: #fff;
}

.site-nav-inner {
  max-width: var(--site-max);
  height: 100%;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.brand {
  display: flex;
  align-items: center;
  flex: none;
}

.brand img {
  display: block;
  height: 34px;
  width: auto;
}

.links {
  display: flex;
  align-items: center;
  gap: 34px;
  margin: 0 auto;
}

.link {
  position: relative;
  padding: 6px 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #1f2632;
  transition: color 0.25s;
}

.link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 3px;
  border-radius: 2px;
  background: var(--site-orange);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.link:hover {
  color: var(--site-orange);
}

.link:hover::after,
.link.is-active::after {
  transform: scaleX(1);
}

.link.is-active {
  color: var(--site-ink);
  font-weight: 700;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 不叫 .btn：全域 main.css 的 .btn 是藍底，會蓋掉這裡的樣式 */
.nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 45px;
  padding: 0 26px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  transition:
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1),
    background-color 0.25s,
    color 0.25s,
    box-shadow 0.25s;
}

.nav-acc {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 10px;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 400;
  white-space: nowrap;
  color: var(--site-ink);
  transition: color 0.25s;
}

.nav-acc:hover {
  color: var(--site-orange);
}

.acc-ico {
  display: block;
  flex: none;
  width: 22px;
  height: 22px;
  object-fit: contain;
}

/* 未登入時的「網路票選登入」：與實心鈕同高的橘色外框膠囊 */
.nav-btn-line {
  padding: 0 12px;
  border: 2px solid var(--site-orange);
  background: transparent;
  color: var(--site-orange);
  font-family: inherit;
  cursor: pointer;
}

.nav-btn-line:hover {
  background: var(--site-orange);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(var(--site-orange-rgb), 0.28);
}

.nav-btn-solid {
  background: var(--site-orange);
  color: #fff;
  box-shadow: 0 6px 16px rgba(var(--site-orange-rgb), 0.28);
}

.nav-btn-solid:hover {
  background: var(--site-orange-deep);
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(var(--site-orange-rgb), 0.36);
}

.toggle {
  display: none;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.toggle span,
.toggle span::before,
.toggle span::after {
  display: block;
  width: 24px;
  height: 2px;
  border-radius: 2px;
  background: var(--site-ink);
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.toggle span {
  margin: 0 auto;
  position: relative;
}

.toggle span::before,
.toggle span::after {
  content: '';
  position: absolute;
  left: 0;
}

.toggle span::before {
  top: -8px;
}

.toggle span::after {
  top: 8px;
}

.toggle span.is-open {
  transform: rotate(45deg);
}

.toggle span.is-open::before {
  transform: translateY(8px) rotate(-90deg);
}

.toggle span.is-open::after {
  transform: translateY(-8px) rotate(-90deg);
  opacity: 0;
}

.actions-m {
  display: none;
}

@keyframes navDrop {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 1080px) {
  .site-nav-inner {
    gap: 16px;
  }

  .links {
    gap: 22px;
  }

  .nav-btn {
    height: 38px;
    padding: 0 16px;
    font-size: 14px;
  }

  .nav-acc {
    padding: 0;
    font-size: 14px;
  }
}

@media (max-width: 900px) {
  .site-head {
    --site-nav-h: 60px;
    --site-topbar-h: 34px;
  }

  .site-top-inner {
    justify-content: center;
    font-size: 12px;
  }

  .site-nav-inner {
    gap: 12px;
    justify-content: space-between;
  }

  .toggle,
  .actions-m {
    display: block;
  }

  .actions {
    display: none;
  }

  .links {
    position: absolute;
    top: var(--site-nav-h);
    left: 0;
    right: 0;
    margin: 0;
    padding: 0 20px;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    background: #fff;
    box-shadow: 0 16px 30px rgba(var(--site-shadow-rgb), 0.12);
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.4s cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .links.is-open {
    max-height: 420px;
  }

  .link {
    padding: 15px 0;
    border-bottom: 1px solid #eef1f4;
    font-size: 16px;
  }

  .link::after {
    width: 22px;
    bottom: 8px;
  }

  .actions-m {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 0 20px;
  }

  .actions-m .nav-btn {
    flex: 1;
    height: 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .site-head {
    animation: none;
  }
}
</style>
